import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';

function TwoFactorVerification() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout, check2FAStatus } = useAuth();
  const functions = getFunctions();
  const inputRef = useRef(null);
  
  const maxAttempts = 5;
  const cooldownTime = 60; // seconds

  useEffect(() => {
    // Focus on input when component mounts
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // DON'T automatically send code - user already got one during login
    // Just show a message that code was sent
    setMessage('Verification code sent to your email');
  }, []);

  useEffect(() => {
    // Countdown timer for resend cooldown
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    // Auto-submit when 6 digits are entered
    if (code.length === 6 && !isVerifying) {
      handleVerify();
    }
  }, [code, isVerifying]);

  const handleSendCode = async () => {
    if (!currentUser) return;
    
    setIsResending(true);
    setError('');
    setMessage('');
    
    try {
      const sendTwoFactorCode = httpsCallable(functions, 'sendTwoFactorCode');
      await sendTwoFactorCode();
      
      setMessage('Verification code sent to your email');
      setResendCooldown(cooldownTime);
      
      // Clear any existing code
      setCode('');
      setAttempts(0);
      
    } catch (error) {
      console.error('Failed to send 2FA code:', error);
      
      if (error.code === 'functions/resource-exhausted' || error.message.includes('Please wait')) {
        // Extract the wait time from the error message if possible
        const waitMatch = error.message.match(/wait (\d+) seconds/);
        const waitTime = waitMatch ? waitMatch[1] : '60';
        setError(`Please wait ${waitTime} seconds before requesting another code.`);
      } else if (error.code === 'functions/unauthenticated') {
        setError('Session expired. Please log in again.');
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else {
        setError('Failed to send verification code. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = async () => {
    if (!code || code.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    if (attempts >= maxAttempts) {
      setError('Too many failed attempts. Please request a new code.');
      return;
    }

    setIsVerifying(true);
    setError('');
    setMessage('');

    try {
      const verifyTwoFactorCode = httpsCallable(functions, 'verifyTwoFactorCode');
      const result = await verifyTwoFactorCode({ code });

      if (result.data.success) {
        // Force 2FA status recheck immediately (this will set is2FAVerified to true)
        await check2FAStatus(currentUser);
        
        // Get the intended destination from location state or default to home
        const from = location.state?.from?.pathname || '/';
        const searchParams = location.state?.preserveSearch && location.state?.from ? 
          new URLSearchParams(location.state.from.search).toString() : '';
        
        const destination = from + (searchParams ? `?${searchParams}` : '');
        
        // Navigate immediately - no message, no delay
        navigate(destination, { replace: true });
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        
        if (result.data.error === 'expired') {
          setError('Verification code has expired. Please request a new one.');
        } else if (result.data.error === 'invalid') {
          const remaining = maxAttempts - newAttempts;
          if (remaining > 0) {
            setError(`Invalid code. ${remaining} attempts remaining.`);
          } else {
            setError('Too many failed attempts. Please request a new code.');
          }
        } else {
          setError('Verification failed. Please try again.');
        }
        
        // Clear the code input for retry
        setCode('');
      }
    } catch (error) {
      console.error('2FA verification failed:', error);
      
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      if (error.code === 'functions/unauthenticated') {
        setError('Session expired. Please log in again.');
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else {
        const remaining = maxAttempts - newAttempts;
        if (remaining > 0) {
          setError(`Verification failed. ${remaining} attempts remaining.`);
        } else {
          setError('Too many failed attempts. Please request a new code.');
        }
      }
      
      // Clear the code input for retry
      setCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="w-full max-w-md px-4 py-8">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Two-Factor Verification
          </h1>
          <p className="text-gray-600">
            Enter the 6-digit code sent to<br />
            <span className="font-medium">{currentUser.email}</span>
          </p>
        </div>

        <div className="bg-white shadow-card rounded-lg p-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
              <p className="text-sm">{error}</p>
            </div>
          )}
          
          {message && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-4">
              <p className="text-sm">{message}</p>
            </div>
          )}

          <div className="mb-6">
            <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              ref={inputRef}
              id="verification-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="6"
              className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent tracking-widest"
              value={code}
              onChange={handleCodeChange}
              placeholder="000000"
              disabled={isVerifying || attempts >= maxAttempts}
              autoComplete="one-time-code"
            />
            <p className="text-xs text-gray-500 mt-2 text-center">
              Enter the 6-digit code from your email
            </p>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleVerify}
              disabled={code.length !== 6 || isVerifying || attempts >= maxAttempts}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                code.length === 6 && attempts < maxAttempts
                  ? 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isVerifying ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </span>
              ) : (
                'Verify Code'
              )}
            </button>

            <div className="text-center">
              <button
                onClick={handleSendCode}
                disabled={isResending || resendCooldown > 0}
                className={`text-sm ${
                  resendCooldown > 0 || isResending
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-blue-600 hover:text-blue-700 hover:underline'
                }`}
              >
                {isResending ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-1 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : resendCooldown > 0 ? (
                  `Resend code in ${resendCooldown}s`
                ) : (
                  'Resend verification code'
                )}
              </button>
            </div>

            <div className="border-t border-gray-200 pt-4 text-center">
              <button
                onClick={handleLogout}
                className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
              >
                Sign out and use a different account
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TwoFactorVerification;